import { createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";
import NotificationsInfo from "../../../ui/modules/notifications/interfaces/NotificationsInfo";
import loading from "../../../core/types/loading";
import { fetchGetExams, fetchGetNotificationsInfo } from "./notificationsThunk";
import Notification from "../../../ui/modules/notifications/interfaces/Notification";
import { ExamsPayload } from "../../../ui/modules/notifications/interfaces/ExamDiscipline";

interface NotificationsState {
  info: NotificationsInfo;
  notifications: Notification[];
  countLoadingStatus: loading;
  exams: ExamsPayload;
  examsCount: number;
  examsLoadingStatus: loading;
}

const getUnpassedExamsCount = (exams: ExamsPayload | null): number => {
  if (!exams) {
    return 0;
  }

  return [...(exams["1"] ?? []), ...(exams["2"] ?? [])].filter((exam) => !!exam.token).length;
};

const initialState: NotificationsState = {
  info: null,
  notifications: [],
  countLoadingStatus: null,
  exams: null,
  examsCount: 0,
  examsLoadingStatus: null,
};

const notificationsAdapter = createEntityAdapter<NotificationsState>();

const NotificationsSlice = createSlice({
  name: "notifications",
  initialState: notificationsAdapter.getInitialState(initialState),
  reducers: {
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const notificationId = action.payload;
      const newNotifications = [...state.notifications];

      newNotifications.splice(
        newNotifications.findIndex((notification) => notification.uid === notificationId),
        1
      );

      state.notifications = newNotifications;
    },
  },
  extraReducers: (builder) => {
    // GET COUNT
    builder.addCase(fetchGetNotificationsInfo.pending, (state) => {
      state.countLoadingStatus = "LOADING";
    });
    builder.addCase(fetchGetNotificationsInfo.fulfilled, (state, action) => {
      if (action.payload.error) {
        state.countLoadingStatus = "ERROR";
        return;
      }
      state.countLoadingStatus = "COMPLETE";

      state.info = action.payload.payload;
    });

    // GET EXAMS
    builder.addCase(fetchGetExams.pending, (state) => {
      state.examsLoadingStatus = "LOADING";
    });
    builder.addCase(fetchGetExams.fulfilled, (state, action) => {
      if (action.payload.error) {
        state.examsLoadingStatus = "ERROR";
        return;
      }
      state.examsLoadingStatus = "COMPLETE";
      state.exams = action.payload.payload ?? null;
      state.examsCount = getUnpassedExamsCount(action.payload.payload ?? null);
    });
  },
});

export const { setNotifications, removeNotification } = NotificationsSlice.actions;

export default NotificationsSlice.reducer;
